import { ClipboardList, FileText, MapPin, Pencil, Plus, Printer, StickyNote, User, X } from "lucide-react";
import { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { StatusBadge, type StatusTone } from "@fawkes/ui";

import { formatDateTime } from "../../i18n/format";
import type { Dossier } from "./types";

/**
 * De kop van het dossier: het nummer, waar de aanvraag staat, en de drie kaarten eronder.
 *
 * <p><b>Drie dingen naast elkaar, in de volgorde waarin je ze nodig hebt</b> (mockup Maarten,
 * 26-10-2026). Wie is het? De aanvrager. Waarom? De reden. Wat doen we ermee? De status. Een
 * medewerker die een aanvraag oppakt leest ze in die volgorde, en hoeft daarvoor niet te scrollen of
 * eerst een tabblad te kiezen.
 *
 * <p><b>De reden staat in woorden en niet als tabelrij.</b> Hij komt uit de vragenlijst, maar het is
 * het eerste wat iemand wil lezen — hem tussen veertig andere antwoorden zetten maakt hem onvindbaar.
 */

const TOON: Record<string, StatusTone> = { ROOD: "danger", ORANJE: "warning", GROEN: "success" };

export function WachtlijstDossierKop({
  dossier, locale, statusKeuze, onAfdrukken, onNotitie, onBehandelaar, onLabelErbij, onLabelWeg,
  vestigingBlok, klantBlok, bezig,
}: {
  dossier: Dossier;
  locale: string;
  /** De statuskeuze met zijn knop; die hoort bij het scherm dat de wijziging wegschrijft. */
  statusKeuze: React.ReactNode;
  onAfdrukken: () => void;
  onNotitie: () => void;
  /** Leeg maakt de aanvraag weer vrij; een naam zet hem op die medewerker. */
  onBehandelaar: (naam: string | null) => void;
  onLabelErbij: (label: string) => void;
  onLabelWeg: (label: string) => void;
  vestigingBlok?: React.ReactNode;
  klantBlok?: React.ReactNode;
  bezig: boolean;
}) {
  const t = useTranslate();
  const { regel, contact } = dossier;
  const [nieuwLabel, setNieuwLabel] = useState("");
  const [labelOpen, setLabelOpen] = useState(false);

  return (
    <>
      <header className="wl-kop">
        <div className="wl-kop__titel">
          <h2>
            {regel.nummer ?? "—"}
            <StatusBadge tone={TOON[regel.stoplicht] ?? "neutral"}>
              {t(`wachtlijst.status.${regel.status}`)}
            </StatusBadge>
          </h2>
          <p className="wl-kop__ontvangen">
            {t("wachtlijstDossier.ontvangenOp")} {formatDateTime(regel.ontvangenOp, locale)}
          </p>
        </div>
        <div className="wl-kop__acties">
          <button type="button" className="wl-kop__knop" onClick={onAfdrukken}>
            <Printer size={15} aria-hidden="true" />
            {t("wachtlijstDossier.afdrukken")}
          </button>
          <button type="button" className="wl-kop__knop" onClick={onNotitie}>
            <StickyNote size={15} aria-hidden="true" />
            {t("wachtlijstDossier.notitieToevoegen")}
          </button>
        </div>
      </header>

      <div className="wl-kaarten">
        <section className="wl-kaart wl-kaart--kop">
          <h3><User size={16} aria-hidden="true" />{t("wachtlijstDossier.aanvrager")}</h3>
          <dl className="wl-gegevens wl-gegevens--kop">
            <Regel label={t("wachtlijstDossier.naam")} waarde={contact?.naam} />
            <Regel label={t("wachtlijstDossier.email")} waarde={contact?.email} />
            <Regel label={t("wachtlijstDossier.telefoon")} waarde={contact?.telefoon} />
            <Regel
              label={t("wachtlijstDossier.adres")}
              waarde={[
                [contact?.straat, contact?.huisnummer].filter(Boolean).join(" "),
                [contact?.postcode, contact?.woonplaats].filter(Boolean).join(" "),
              ].filter(Boolean).join(", ")}
            />
          </dl>
          {klantBlok && <div className="wl-dos__klant">{klantBlok}</div>}
        </section>

        <section className="wl-kaart wl-kaart--kop">
          <h3><FileText size={16} aria-hidden="true" />{t("wachtlijstDossier.redenVanAfstand")}</h3>
          {/* Geen reden is geen fout: niet elke vragenlijst stelt die vraag. Dan staat er wat er wél
              is, in plaats van een leeg vak dat eruitziet alsof er iets stuk is. */}
          <p className="wl-kaart__tekst">
            {dossier.redenVanAfstand?.trim() || t("wachtlijstDossier.geenReden")}
          </p>
          <div className="wl-kaart__tweeluik">
            <div>
              <h4><ClipboardList size={14} aria-hidden="true" />{t("wachtlijstDossier.gewenstePeriode")}</h4>
              <p>{dossier.gewenstePeriode?.trim() || "—"}</p>
            </div>
            <div>
              <h4><MapPin size={14} aria-hidden="true" />{t("wachtlijstDossier.verblijftNuBij")}</h4>
              <p>
                {regel.klantNaam
                  ? t("wachtlijstDossier.bijDeRelatie", { args: [regel.klantNaam] })
                  : contact?.naam || t("wachtlijstDossier.bijDeEigenaar")}
              </p>
            </div>
          </div>
        </section>

        <section className="wl-kaart wl-kaart--kop">
          <h3><ClipboardList size={16} aria-hidden="true" />{t("wachtlijstDossier.status")}</h3>
          {statusKeuze}

          <h4 className="wl-kaart__tussenkop">{t("wachtlijstDossier.behandelaar")}</h4>
          <div className="wl-behandelaar">
            <span className={dossier.behandelaar ? undefined : "fawkes-muted"}>
              {dossier.behandelaar ?? t("wachtlijstDossier.nogNiemand")}
            </span>
            {/* Aan mij toewijzen of vrijgeven: de twee dingen die een medewerker echt doet. Een
                keuzelijst van alle collega's zou werk maken van iets wat zelden nodig is. */}
            <button type="button" className="wl-klant__knop" disabled={bezig}
                    onClick={() => onBehandelaar(dossier.behandelaar ? null : "")}>
              <Pencil size={14} aria-hidden="true" />
              {dossier.behandelaar
                ? t("wachtlijstDossier.vrijgeven")
                : t("wachtlijstDossier.aanMijToewijzen")}
            </button>
          </div>

          <h4 className="wl-kaart__tussenkop">{t("wachtlijstDossier.labels")}</h4>
          <div className="wl-labels">
            {dossier.labels.map((l) => (
              <span key={l} className="wl-label">
                {l}
                <button type="button" onClick={() => onLabelWeg(l)} disabled={bezig}
                        aria-label={t("wachtlijstDossier.labelWeg", { args: [l] })}>
                  <X size={12} aria-hidden="true" />
                </button>
              </span>
            ))}
            {labelOpen ? (
              <form
                className="wl-label__nieuw"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (nieuwLabel.trim()) onLabelErbij(nieuwLabel.trim());
                  setNieuwLabel("");
                  setLabelOpen(false);
                }}
              >
                <input value={nieuwLabel} autoFocus maxLength={60}
                       placeholder={t("wachtlijstDossier.labelPlaceholder")}
                       onChange={(e) => setNieuwLabel(e.target.value)}
                       onBlur={() => { setNieuwLabel(""); setLabelOpen(false); }} />
              </form>
            ) : (
              <button type="button" className="wl-klant__knop" disabled={bezig}
                      onClick={() => setLabelOpen(true)}>
                <Plus size={14} aria-hidden="true" />
                {t("wachtlijstDossier.labelToevoegen")}
              </button>
            )}
          </div>

          {vestigingBlok && <div className="wl-dos__vestiging">{vestigingBlok}</div>}
        </section>
      </div>
    </>
  );
}

/** Eén regel; een leeg veld krijgt een streepje, want de ruimte eronder hoort niet te verspringen. */
function Regel({ label, waarde }: { label: string; waarde?: string | null }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{waarde?.trim() ? waarde : "—"}</dd>
    </div>
  );
}
